import React, { useState, createContext, useEffect, useCallback } from "react";
import { IUser } from "../interfaces/user-interface";
import { setAuthToken } from "../apis/instance";

interface Props {
  children: React.ReactNode;
}
interface IUserContext {
  user: IUser | undefined;
  setUser: Function;
}
export const UserContext = createContext<IUserContext>({
  user: undefined,
  setUser: () => {},
});

const STORAGE_KEY = "gopomelo.auth.user";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

interface CachedUser {
  user: IUser;
  expiresAt: number;
}

const readCache = (): IUser | undefined => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: CachedUser = JSON.parse(raw);
    if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return undefined;
    }
    return parsed.user;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
};

const writeCache = (user: IUser) => {
  const payload: CachedUser = {
    user,
    expiresAt: Date.now() + CACHE_TTL_MS,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* storage may be full or blocked */
  }
};

const clearCache = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
};

const UserContextProvider: React.FC<Props> = ({ children }) => {
  const [user, setUserState] = useState<IUser | undefined>(() => {
    const cached = readCache();
    if (cached?.idToken) setAuthToken(cached.idToken);
    return cached;
  });

  const setUser = useCallback((value: IUser | undefined) => {
    setUserState(value);
    if (value) {
      writeCache(value);
      if (value.idToken) setAuthToken(value.idToken);
    } else {
      clearCache();
      setAuthToken(undefined);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const { expiresAt } = JSON.parse(raw) as CachedUser;
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        setUser(undefined);
        return;
      }
      const timer = window.setTimeout(() => setUser(undefined), remaining);
      return () => window.clearTimeout(timer);
    } catch {
      setUser(undefined);
    }
  }, [user, setUser]);

  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
